import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid2,
  SelectChangeEvent,
  CircularProgress,
} from '@mui/material';
import { RootState } from '../../../core/store';
import { useDispatch, useSelector } from 'react-redux';
import { StyledDropdown } from '../../../core/components/ui/StyledDropdown';
import { selectPronounDropdownOptions } from '../../../core/store/slices/demographic.slice';
import { useMemberDemographicsQuery } from '../../../core/queries/useDemographicsQuery';
import { EditPersonalInfoRequest } from '../types/accountSettings';
import { useUpdatePersonalInfoMutation } from '../hooks/accountSettingsApi';
import { useApiErrors } from '../../enrollment/hooks/enrollmentHooks';
import { ErrorSource } from '../../../core/constant/model/api/api';
import ApiErrorLabel from '../../../core/components/ui/ApiErrorLabel';
import { clearApiError } from '../../../core/store/slices/apiError.slice';
import { useProfileQuery } from '../../auth/hooks/useProfileQuery';
import toastService from '../../../services/toastService';

const PersonalInfo: React.FC = () => {
  const { isLoading: isDemographicsLoading } = useMemberDemographicsQuery();
  const dispatch = useDispatch();
  
  const memberId = useSelector((state: RootState) => state.basicInfo._id);
  const personalDetails = useSelector((state: RootState) => state.personalDetails);
  const pronounOptions = useSelector(selectPronounDropdownOptions);
  
  const updatePersonalInfoMutation = useUpdatePersonalInfoMutation();
  const { hasApiError, getApiErrorBySource } = useApiErrors();
  
  const [originalValues, setOriginalValues] = useState<EditPersonalInfoRequest>({
    firstName: '',
    lastName: '',
    preferredName: '',
    pronounsList: []
  });
  
  const [formValues, setFormValues] = useState<EditPersonalInfoRequest>({
    firstName: personalDetails.firstName || '',
    lastName: personalDetails.lastName || '',
    preferredName: personalDetails.preferredName || '',
    pronounsList: personalDetails.pronounsList || []
  });
  const profileQuery = useProfileQuery();
  useEffect(() => {
    setOriginalValues({
      firstName: personalDetails.firstName || '',
      lastName: personalDetails.lastName || '',
      preferredName: personalDetails.preferredName || '',
      pronounsList: personalDetails.pronounsList || []
    });
    
    setFormValues({
      firstName: personalDetails.firstName || '',
      lastName: personalDetails.lastName || '',
      preferredName: personalDetails.preferredName || '',
      pronounsList: personalDetails.pronounsList || []
    });
  }, [personalDetails]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePronounsChange = (e: SelectChangeEvent<string | string[]>) => {
    setFormValues((prev) => ({
      ...prev,
      pronounsList: Array.isArray(e.target.value) ? e.target.value : [e.target.value]
    }));
  };

  const hasChanges = (): boolean => {
    if (formValues.firstName !== originalValues.firstName ||
        formValues.lastName !== originalValues.lastName ||
        formValues.preferredName !== originalValues.preferredName) {
      return true;
    }
    
    const originalPronouns = [...(originalValues.pronounsList || [])].sort();
    const currentPronouns = [...(formValues.pronounsList || [])].sort();
    
    if (originalPronouns.length !== currentPronouns.length) {
      return true;
    }
    
    return JSON.stringify(originalPronouns) !== JSON.stringify(currentPronouns);
  };

  const personalInfoError = hasApiError(ErrorSource.UPDATE_PERSONAL_INFO)
    ? getApiErrorBySource(ErrorSource.UPDATE_PERSONAL_INFO)
    : null;

  const handleDismissError = () => {
    dispatch(clearApiError(ErrorSource.UPDATE_PERSONAL_INFO));
  };

  const handleSubmit = async () => {
    try {
      const result =  await updatePersonalInfoMutation.mutateAsync({
        ...formValues,
        memberId
      });
      
      // Update original values after successful save
      setOriginalValues({...formValues});
      
      // Show success message
      // setShowSuccess(true);
      if (result.statusCode === 200) {
        toastService.success('Personal info updated successfully');
        profileQuery.refetch();
      } 
    } catch (error) {
      console.error('Failed to update personal info:', error);
      // Error will be handled by the API error system
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h5' fontWeight={600} sx={{ mb: 4 }}>
        Personal Information
      </Typography>

      <Grid2 container spacing={3}>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <Typography sx={{ mb: 1 }} fontWeight={500}>
            First Name
          </Typography>
          <TextField
            fullWidth
            name="firstName"
            value={formValues.firstName}
            onChange={handleInputChange}
            variant="outlined"
            disabled={updatePersonalInfoMutation.isLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1
              }
            }}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, sm: 6}}>
          <Typography sx={{ mb: 1 }} fontWeight={500}>
            Last Name
          </Typography>
          <TextField
            fullWidth
            name='lastName'
            value={formValues.lastName}
            onChange={handleInputChange}
            variant='outlined'
            disabled={updatePersonalInfoMutation.isLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
              },
            }}
          />
        </Grid2>

        <Grid2 size={{ xs: 12}}>
          <Typography sx={{ mb: 1 }} fontWeight={500}>
            Preferred Name
          </Typography>
          <TextField
            fullWidth
            name='preferredName'
            value={formValues.preferredName}
            onChange={handleInputChange}
            variant='outlined'
            disabled={updatePersonalInfoMutation.isLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
              },
            }}
          />
        </Grid2>

        <Grid2 size={{ xs: 12 }}>
          <Typography sx={{ mb: 1 }} fontWeight={500}>
            Pronouns
          </Typography>
          <StyledDropdown
            label='What are your pronouns?'
            name='pronounsList'
            value={formValues.pronounsList || []}
            multiple={true}
            onChange={handlePronounsChange}
            options={pronounOptions}
            data-id='pronouns-multiple-selection-dropdown'
            sx={{ mb: 3 }}
            disabled={updatePersonalInfoMutation.isLoading || isDemographicsLoading}
            startAdornment={
              isDemographicsLoading ? (
                <CircularProgress size={20} color='inherit' sx={{ mr: 1 }} />
              ) : undefined
            }
          />
        </Grid2>
      </Grid2>

      {personalInfoError && (
        <ApiErrorLabel
          message={personalInfoError.message}
          variant='alert'
          testId='api-error'
          onDismiss={handleDismissError}
          dismissible={true}
          sx={{ mt: 3, mb: 2 }}
        />
      )}

      <Button
        variant='contained'
        color='primary'
        fullWidth
        size='large'
        onClick={handleSubmit}
        disabled={updatePersonalInfoMutation.isLoading || !hasChanges()}
        sx={{
          mt: 4,
          py: 1.5,
          borderRadius: 1,
          bgcolor: '#1a202c',
          '&:hover': {
            bgcolor: '#2d3748',
          },
          textTransform: 'none',
          fontSize: '1rem',
        }}
      >
        {updatePersonalInfoMutation.isLoading ? 'Saving...' : 'Save Changes'}
      </Button>
    </Box>
  );
};

export default PersonalInfo;